'use client';

import { FileJson, Loader2, Sheet } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useExportSelectedStores, type Store } from '@/lib/api/stores';
import { cn } from '@/lib/utils';
import {
  downloadFlatStoresCsv,
  downloadStoreExportBundle,
  type StoreExportFormat,
} from '@/app/stores/utils/export-selected-stores';

type ExportSelectedStoresModalProps = {
  stores: Store[];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

type ExportFormatOptionProps = {
  value: StoreExportFormat;
  selected: boolean;
  title: string;
  description: string;
  icon: React.ReactNode;
  onSelect: (value: StoreExportFormat) => void;
};

const ExportFormatOption = ({
  value,
  selected,
  title,
  description,
  icon,
  onSelect,
}: ExportFormatOptionProps) => (
  <div
    role="button"
    tabIndex={0}
    onClick={() => onSelect(value)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect(value);
      }
    }}
    className={cn(
      'flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors',
      selected
        ? 'border-primary bg-primary/5 ring-1 ring-primary/15'
        : 'border-border hover:border-primary/30 hover:bg-muted/40',
    )}
  >
    <div
      className={cn(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
        selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
      )}
    >
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium leading-tight">{title}</p>
      <p className="text-muted-foreground mt-0.5 text-xs leading-snug">{description}</p>
    </div>
    <RadioGroupItem value={value} id={`store-export-${value}`} className="shrink-0" />
  </div>
);

const runTemplateExport = async (
  stores: Store[],
  mutateAsync: ReturnType<typeof useExportSelectedStores>['mutateAsync'],
) => {
  const bundle = await mutateAsync(stores);
  downloadStoreExportBundle(bundle);
  return bundle.count;
};

export const ExportSelectedStoresModal = ({
  stores,
  open,
  onClose,
  onSuccess,
}: ExportSelectedStoresModalProps) => {
  const { t } = useTranslation('stores');
  const { t: tCommon } = useTranslation('common');
  const exportTemplatesMutation = useExportSelectedStores();
  const [format, setFormat] = React.useState<StoreExportFormat>('template');

  React.useEffect(() => {
    if (open) setFormat('template');
  }, [open]);

  const handleExport = async () => {
    try {
      if (format === 'summary') {
        downloadFlatStoresCsv(stores);
        toast.success(t('table.exportDialog.summarySuccess', { count: stores.length }));
      } else {
        const count = await runTemplateExport(stores, exportTemplatesMutation.mutateAsync);
        toast.success(t('table.exportDialog.templateSuccess', { count }));
      }
      onSuccess?.();
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('table.exportDialog.error');
      toast.error(message);
    }
  };

  const isPending = exportTemplatesMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-w-md gap-0 p-0">
        <DialogHeader className="space-y-1 border-b px-6 py-5 text-left">
          <DialogTitle>{t('table.exportDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('table.exportDialog.description', { count: stores.length })}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4">
          <RadioGroup
            value={format}
            onValueChange={(value) => setFormat(value as StoreExportFormat)}
            className="grid gap-2"
          >
            <ExportFormatOption
              value="template"
              selected={format === 'template'}
              title={t('table.exportDialog.templateTitle')}
              description={t('table.exportDialog.templateDescription')}
              icon={<FileJson className="h-4 w-4" />}
              onSelect={setFormat}
            />
            <ExportFormatOption
              value="summary"
              selected={format === 'summary'}
              title={t('table.exportDialog.summaryTitle')}
              description={t('table.exportDialog.summaryDescription')}
              icon={<Sheet className="h-4 w-4" />}
              onSelect={setFormat}
            />
          </RadioGroup>
        </div>

        <DialogFooter className="border-t px-6 py-4">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {tCommon('cancel')}
          </Button>
          <Button onClick={() => void handleExport()} disabled={isPending || stores.length === 0}>
            {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('table.exportDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
